<script setup lang="ts">
import { nextTick } from 'vue'
import { http } from '@/utils/http'
import { customersEvent } from '~/utils/track/customer'

const { t } = useLang()
const { locale } = useI18n()
const route = useRoute()
const siteCookie = useCookie<string>('hg_site')
const currentSiteCode = computed(() =>
  String(route.query.siteCode || route.query.site || siteCookie.value || 'HK').trim().toUpperCase() || 'HK',
)
const hasFaqIdParam = computed(() => route.params.id !== undefined)
const faqId = computed(() => getNumberValue(route.params.id))

const requestUrl = useRequestURL()
const runtimeConfig = useRuntimeConfig()

function getCanonicalOrigin() {
  const envOrigin = runtimeConfig.public.envData.VITE_API_HOST || ''
  return /^https?:\/\//.test(envOrigin) ? envOrigin.replace(/\/$/, '') : requestUrl.origin
}

const faqQuery = computed(() => {
  const query = new URLSearchParams()
  const firstCategoryId = getNumberValue(route.query.firstCategoryId)
  const secondCategoryId = getNumberValue(route.query.secondCategoryId)
  if (firstCategoryId) {
    query.set('firstCategoryId', String(firstCategoryId))
  }
  if (secondCategoryId) {
    query.set('secondCategoryId', String(secondCategoryId))
  }
  return query.toString()
})

function getFaqUrl(targetLocale: string) {
  if (faqId.value) {
    return `${getCanonicalOrigin()}/${targetLocale}/common/faq/${faqId.value}`
  }

  const pathname = `/${targetLocale}/common/faq`
  return `${getCanonicalOrigin()}${pathname}${faqQuery.value ? `?${faqQuery.value}` : ''}`
}

interface SecondCategoryItem {
  id: number
  name: string
  cnName?: string
  sortNum?: number
}

interface MenuItem {
  id?: number
  categoryId?: number
  name?: string
  categoryName?: string
  secondCategoryList?: SecondCategoryItem[]
}
interface FaqItem {
  questionTitle: string
  answerContent: string
  questionId: number
  categoryId?: number
  secondCategoryId?: number
  opened?: boolean
  rated?: boolean
  id?: number
  secondCategoryName?: string
  firstCategoryName?: string
}
interface FaqApiResponse<T = any> {
  status?: number
  body?: T
}
interface FaqSsrData {
  categoryList: MenuItem[]
  faqList: FaqItem[]
  selectedCategoryId?: number
  selectedSecondCategoryId?: number
  title?: string
  requestedQuestionFound?: boolean
}

const bigTitle = ref('')
const faqSsrRequestHeaders = {
  'HopeGoo-Locale': locale.value,
  'HopeGoo-Platform': 'PC',
  'HopeGoo-Site': currentSiteCode.value,
}
const emptyFaqSsrData: FaqSsrData = {
  categoryList: [],
  faqList: [],
}

function getNumberValue(value: any) {
  const numberValue = Number(value)
  return (Number.isInteger(numberValue) && numberValue > 0) ? numberValue : undefined
}

function mapCategoryMenus(categoryList: MenuItem[]): MenuItem[] {
  return categoryList.map((item: any) => ({
    id: item.categoryId ?? item.id,
    name: item.categoryName ?? item.name,
    secondCategoryList: item.secondCategoryList,
  }))
}

function normalizeFaqList(body: any): FaqItem[] {
  if (Array.isArray(body)) {
    return body
  }

  return body?.faqQuestionDtos || []
}

function fetchFaqSsrApi<T = any>(url: string, body: any) {
  return http({
    url: `/sea_shell${url}`,
    method: 'POST',
    body,
    headers: faqSsrRequestHeaders,
  }) as Promise<FaqApiResponse<T>>
}

const { data: faqSsrData } = await useAsyncData(
  `faq-basic-ssr-${locale.value}-${currentSiteCode.value}-${faqId.value || ''}-${route.query.firstCategoryId || ''}-${route.query.secondCategoryId || ''}`,
  async () => {
    try {
      const categoryRes = await fetchFaqSsrApi<MenuItem[]>('/sea_turtle_api/helpCenter/allCategory', {
        platform: 1,
      })

      if (categoryRes?.status !== 200) {
        return emptyFaqSsrData
      }

      const categoryList = categoryRes?.body || []
      const slidebarMenuList = mapCategoryMenus(categoryList)
      const faqCookie = useCookie<any>('faq').value || {}
      const routeCategoryId = getNumberValue(route.query.firstCategoryId)
      const routeSecondCategoryId = getNumberValue(route.query.secondCategoryId)
      const cookieCategoryId = getNumberValue(faqCookie.categoryId)
      const cookieSecondCategoryId = getNumberValue(faqCookie.secondCategoryId)
      const routeCategory = slidebarMenuList.find(category => category.id === routeCategoryId)
      const cookieCategory = (!routeCategory && route.query.cookie)
        ? slidebarMenuList.find(category => category.id === cookieCategoryId)
        : undefined
      const defaultCategory = slidebarMenuList[0]
      let selectedCategoryItem = routeCategory || cookieCategory || defaultCategory
      let selectedFaqList: FaqItem[] | undefined
      let selectedFaqSecondCategoryId: number | undefined

      if (faqId.value) {
        const categoryFaqResults = await Promise.all(slidebarMenuList.map(async (category) => {
          const response = await fetchFaqSsrApi('/sea_turtle_api/helpCenter/qaListByFirstCategory', {
            firstCategoryId: category.id,
            platFormNo: 1,
          })
          return {
            category,
            response,
            faqList: response?.status === 200 ? normalizeFaqList(response.body) : [],
          }
        }))
        const matchedResult = categoryFaqResults.find(result =>
          result.faqList.some(item => item.id === faqId.value),
        )

        if (!matchedResult) {
          return {
            ...emptyFaqSsrData,
            categoryList,
            requestedQuestionFound: false,
          }
        }

        selectedCategoryItem = matchedResult.category
        const matchedQuestion = matchedResult.faqList.find(item => item.id === faqId.value)
        selectedFaqSecondCategoryId = matchedQuestion?.secondCategoryId
        selectedFaqList = selectedFaqSecondCategoryId
          ? matchedResult.faqList.filter(item => item.secondCategoryId === selectedFaqSecondCategoryId)
          : matchedResult.faqList
      }

      const selectedSecondCategoryIdFromRequest = faqId.value
        ? selectedFaqSecondCategoryId
        : (routeCategory ? routeSecondCategoryId : cookieSecondCategoryId)
      const selectedSecondCategory = typeof selectedSecondCategoryIdFromRequest === 'number'
        ? selectedCategoryItem?.secondCategoryList?.find(item => item.id === selectedSecondCategoryIdFromRequest)
        : undefined
      const selectedSecondCategoryId = selectedSecondCategory?.id
      const useSecondCategory = typeof selectedSecondCategoryId === 'number'
        && (!!faqId.value || !!routeCategory || !!cookieCategory)

      if (!selectedCategoryItem?.id) {
        return {
          ...emptyFaqSsrData,
          categoryList,
        }
      }

      let faqRes: FaqApiResponse | undefined

      if (selectedFaqList) {
        faqRes = undefined
      }
      else if (useSecondCategory) {
        faqRes = await fetchFaqSsrApi('/sea_turtle_api/helpCenter/qaListBySecondCategory', {
          firstCategoryId: selectedCategoryItem.id,
          secondCategoryId: selectedSecondCategoryId,
          platFormNo: 1,
        })
      }
      else {
        faqRes = await fetchFaqSsrApi('/sea_turtle_api/helpCenter/qaListByFirstCategory', {
          firstCategoryId: selectedCategoryItem.id,
          platFormNo: 1,
        })
      }

      if (!selectedFaqList && faqRes?.status !== 200) {
        return {
          ...emptyFaqSsrData,
          categoryList,
        }
      }

      const faqList = selectedFaqList || normalizeFaqList(faqRes?.body)
      const openedQuestionId = faqId.value || faqList[0]?.id

      return {
        categoryList,
        faqList: faqList.map(item => ({
          ...item,
          opened: item.id === openedQuestionId,
        })),
        selectedCategoryId: selectedCategoryItem.id,
        selectedSecondCategoryId: useSecondCategory
          ? selectedSecondCategoryId
          : selectedCategoryItem.secondCategoryList?.[0]?.id,
        title: useSecondCategory
          ? (selectedSecondCategory?.name || '')
          : (selectedCategoryItem.name || ''),
        requestedQuestionFound: faqId.value ? true : undefined,
      }
    }
    catch {
      return emptyFaqSsrData
    }
  },
  {
    default: () => emptyFaqSsrData,
  },
)

if (hasFaqIdParam.value && (!faqId.value || faqSsrData.value?.requestedQuestionFound === false)) {
  throw createError({ statusCode: 404, statusMessage: 'FAQ not found' })
}

useHead(() => {
  const links: Record<string, string>[] = [
    { key: 'canonical', rel: 'canonical', href: getFaqUrl(locale.value) },
  ]

  if (!faqId.value && !faqQuery.value && ['en-hk', 'zh-hk'].includes(locale.value)) {
    links.push(
      { key: 'faq-hreflang-en', rel: 'alternate', hreflang: 'en', href: getFaqUrl('en-hk') },
      { key: 'faq-hreflang-zh-hk', rel: 'alternate', hreflang: 'zh-HK', href: getFaqUrl('zh-hk') },
    )
  }

  return {
    title: t('faq.b_tdk_title_seo'),
    meta: [
      { name: 'keywords', content: t('common.b_tdk_keywords') },
      { name: 'description', content: t('faq.b_tdk_desc_seo') },
      { name: 'author', content: t('common.b_author') },
      { name: 'copyright', content: t('common.b_company') },
    ],
    link: links,
    script: [
      {
        src: '/intlPC/js/jquery-3.3.1.min.js',
        type: 'text/javascript',
      },
    ],
  }
})

const initialFaqSsrData = faqSsrData.value || emptyFaqSsrData
const initialSlidebarMenus = mapCategoryMenus(initialFaqSsrData.categoryList)
const initialCurMenu = initialSlidebarMenus.find(item => item.id === initialFaqSsrData.selectedCategoryId) || {}
const initialCurSecondMenu = initialCurMenu.secondCategoryList?.find(item => item.id === initialFaqSsrData.selectedSecondCategoryId) || initialCurMenu.secondCategoryList?.[0]
const hasInitialFaqSsrData = initialFaqSsrData.faqList.length > 0 && initialSlidebarMenus.length > 0

if (hasInitialFaqSsrData) {
  bigTitle.value = initialFaqSsrData.title || initialCurMenu.name || ''
}

const slidebarMenus = ref<MenuItem[]>(initialSlidebarMenus)
const curMenu = ref<MenuItem>(initialCurMenu)
const curSecondMenu = ref<Required<MenuItem>['secondCategoryList'][0] | undefined>(initialCurSecondMenu)
const faqList = ref<FaqItem[]>(initialFaqSsrData.faqList)
const rateBtnRefs = ref<Record<number, HTMLElement>>({})
const rateButtonWidth = ref<number>()
const rateButtonStyle = computed(() => rateButtonWidth.value
  ? {
      '--rate-button-width': rateButtonWidth.value.toString().concat('px'),
      '--rate-button-helpful-width': (rateButtonWidth.value + 8).toString().concat('px'),
    }
  : {})

function setRateBtnRef(element: Element | null, index: number) {
  if (element instanceof HTMLElement) {
    rateBtnRefs.value[index] = element
  }
  else {
    delete rateBtnRefs.value[index]
  }
}

function updateRateButtonWidth() {
  rateButtonWidth.value = undefined
  nextTick(() => {
    const rateButton = Object.values(rateBtnRefs.value).find(element => element.offsetParent !== null)
    const buttons = rateButton?.querySelectorAll<HTMLButtonElement>('.rate-btn__item')
    if (!buttons?.length) {
      return
    }

    rateButtonWidth.value = Math.max(
      ...Array.from(buttons, button => button.scrollWidth),
    )
  })
}

watch(
  [faqList, () => locale.value],
  updateRateButtonWidth,
  { deep: true, flush: 'post' },
)

const pageConfig = reactive({
  attr: {
    total: 0,
    pageSize: 10,
    currentPage: 1,
  },
  on: {
    currentChange: (value: number) => {
      pageConfig.attr.currentPage = value
      queryFaqList()
    },
  },
})

// 初始化
function init() {
  const cookie = route.query.cookie
  if (!cookie) {
    useCookie<any>('faq').value = {}
  }
  const { categoryId = '', secondCategoryId = '', questionId = '' } = cookie ? (useCookie('faq').value as any || {}) : {}
  queryLeftMenus().then(() => {
    curMenu.value = typeof categoryId === 'number' ? (slidebarMenus.value.find(item => item.id === Number(categoryId)) || slidebarMenus.value[0]) : (slidebarMenus.value[0] || {})
    curSecondMenu.value = typeof secondCategoryId === 'number' ? (curMenu.value.secondCategoryList?.find(item => item.id === Number(secondCategoryId)) || curMenu.value.secondCategoryList?.[0]) : curMenu.value.secondCategoryList?.[0]
    if (typeof secondCategoryId !== 'number') {
      queryFaqList()
    }
    else {
      querySecondFaqList().then(() => {
        faqList.value.forEach((item) => {
          if (item.id === Number(questionId)) {
            item.opened = true
          }
        })
      })
    }
  })
}
// 获取左侧菜单
async function queryLeftMenus() {
  try {
    const res = await http({
      url: '/sea_turtle_api/helpCenter/allCategory',
      baseURL: '/sea_shell',
      method: 'POST',
      body: {
        platform: 1,
      },
    })
    if (res?.status === 200) {
      slidebarMenus.value = (res?.body || []).map((item: any) => {
        return {
          id: item.categoryId,
          name: item.categoryName,
          secondCategoryList: item.secondCategoryList,
        }
      })
    }
    else {
      $utils.messageBox(t, res?.msg)
    }
  }
  catch (error: any) {
    $utils.messageBox(t, error?.msg || error?.message)
  }
}
// 获取右侧内容接口数据
async function queryFaqList() {
  bigTitle.value = curMenu.value.name || ''
  const loading = ElLoading.service({ target: '.main_content' })
  try {
    const res = await http({
      url: '/sea_turtle_api/helpCenter/qaListByFirstCategory',
      baseURL: '/sea_shell',
      method: 'post',
      body: {
        firstCategoryId: curMenu.value.id,
        platFormNo: 1,
      },
    })

    if (res?.status === 200 && res?.body) {
      const { body } = res
      faqList.value = body.faqQuestionDtos
      // pageConfig.attr.total = data.totalCount
      window.scrollTo(0, 0)
    }
    else {
      $utils.messageBox(t, res?.msg)
    }
  }
  catch (error: any) {
    $utils.messageBox(t, error?.msg || error?.message)
  }
  finally {
    loading.close()
  }
}

async function querySecondFaqList() {
  bigTitle.value = curSecondMenu.value?.name || ''
  const loading = ElLoading.service({ target: '.main_content' })
  try {
    const res = await http({
      url: '/sea_turtle_api/helpCenter/qaListBySecondCategory',
      baseURL: '/sea_shell',
      method: 'post',
      body: {
        firstCategoryId: curMenu.value.id,
        secondCategoryId: curSecondMenu.value?.id,
        platFormNo: 1,
      },
    })
    if (res?.status === 200 && res?.body) {
      const { body } = res
      faqList.value = body
      // pageConfig.attr.total = data.totalCount
      window.scrollTo(0, 0)
    }
    else {
      $utils.messageBox(t, res?.msg)
    }
  }
  catch (error: any) {
    $utils.messageBox(t, error?.msg || error?.message)
  }
  finally {
    loading.close()
  }
}

/* ====== 用户交互 ====== */
// 点击menu
function handleClickMenu(menuItem: MenuItem) {
  curMenu.value = menuItem
  if (menuItem.secondCategoryList?.length) {
    curSecondMenu.value = menuItem.secondCategoryList[0]
    querySecondFaqList()
  }
  else {
    queryFaqList()
  }
  useCookie('faq').value = { categoryId: menuItem.id } as any
}
// 点击faq
function handleClickFaqAsk(faqItem: FaqItem) {
  faqList.value.forEach((item) => {
    if (item.id !== faqItem.id) {
      item.opened = false
    }
    else {
      item.opened = !item.opened
    }
  })
  if (faqItem.opened) {
    customersEvent.questionShowEvent(
      faqItem.questionTitle,
      faqItem.firstCategoryName || '',
      faqItem.secondCategoryName || '',
    )
  }
}
function handleScondMenuClick(secondCategory: any) {
  curSecondMenu.value = secondCategory
  useCookie('faq').value = { categoryId: curMenu.value.id, secondCategoryId: curSecondMenu.value?.id } as any
  querySecondFaqList()
}
function rate(helpful: number, currentQuestion: any) {
  http({
    url: '/sea_turtle_api/helpCenter/questionEvaluate',
    baseURL: '/sea_shell',
    method: 'post',
    body: {
      questionId: currentQuestion.id,
      evaluteRes: helpful,
    },
  }).then(() => {
    currentQuestion.rated = true
  })
  customersEvent.questionClickEvent(
    currentQuestion.questionTitle,
    currentQuestion.firstCategoryName,
    currentQuestion.secondCategoryName,
    helpful ? 'helpful' : 'not helpful',
  )
}

/* ====== 生命周期 ====== */
onMounted(() => {
  updateRateButtonWidth()
  setTimeout(() => {
    if (!hasInitialFaqSsrData) {
      init()
    }
  })
})
</script>

<template>
  <div class="maincontent">
    <div class="content clearfix">
      <div class="left_nav">
        <ul class="slidebar">
          <li
            v-for="menuItem of slidebarMenus"
            :key="menuItem.id"
            :categoryId="menuItem.id"
          >
            <div class="first-name" :class="{ active: (curMenu?.id === menuItem.id) && !menuItem.secondCategoryList }" @click="handleClickMenu(menuItem)">
              {{ menuItem.name }}
            </div>
            <div v-if="menuItem.id === curMenu.id">
              <div v-for="secondCategory in menuItem.secondCategoryList" :key="secondCategory.id" class="second-name" :class="{ active: curSecondMenu?.id === secondCategory.id }" @click="handleScondMenuClick(secondCategory)">
                {{ secondCategory.name }}
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div class="right_main">
        <div class="faq_info">
          <h1 class="m-0 title">
            {{ bigTitle }}
          </h1>
          <div class="main_content">
            <ul class="faq_ul">
              <li
                v-for="(faqItem, index) of faqList" :key="index"
                class="faq_li" :class="{ 'is-open': faqItem.opened }"
              >
                <div
                  class="faq_ask"
                  :data-index="index + 1"
                  :data-maintitle="curMenu.name"
                  :data-title="faqItem.questionTitle"
                  @click="handleClickFaqAsk(faqItem)"
                >
                  <em class="">{{ faqItem.questionTitle }}</em>
                  <span class="arrow" :class="{ open_view: faqItem.opened }" />
                </div>
                <div v-show="faqItem.opened" class="faq_answer">
                  <div v-html="faqItem.answerContent" />
                  <ClientOnly>
                    <div class="dash-divider" />
                    <div class="flex justify-center mt-27px">
                      <div
                        v-if="!faqItem.rated"
                        :ref="element => setRateBtnRef(element, index)"
                        class="rate-btn"
                        :style="rateButtonStyle"
                      >
                        <button
                          type="button"
                          class="rate-btn__item rate-btn__item--helpful"
                          @click="rate(1, faqItem)"
                        >
                          {{ t('questions.p_question_button_helpful') }}
                        </button>
                        <button
                          v-long-touch-animation:active
                          type="button"
                          class="rate-btn__item rate-btn__item--not-helpful"
                          @click="rate(0, faqItem)"
                        >
                          {{ t('questions.p_question_button_not_helpful') }}
                        </button>
                      </div>
                      <div v-else class="rate-thanks">
                        {{ t('questions.p_question_thanks') }}
                      </div>
                    </div>
                  </ClientOnly>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <!-- <div v-if="faqList.length" class="faq_page">
          <el-pagination
            background layout="prev, pager, next"
            v-bind="pageConfig.attr"
            v-on="pageConfig.on"
          />
        </div> -->
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.content {
    width: 1190px;
    margin: 0 auto;
}

.clearfix:after {
    color: transparent;
}

.left_nav {
    float: left;
    width: 215px;
    margin: 58px 0 0 35px;
    font-family: regular
}

.left_nav ul>li {
    padding-bottom: 28px;
    font-size: 18px;
    line-height: 18px;
    cursor: default;
}

.left_nav a {
    text-decoration: none;
    color: #333;
}

.left_nav a:hover {
    color: var(--Brand-Green);
}

.left_nav .active {
    color: var(--Brand-Green);
    font-weight: 600;
}

.first-name{
  font-size: 18px;
  font-weight: bold;
}
.second-name{
  font-size: 14px;
  margin-left: 16px;
  margin-top: 22px;
}

.first-name,.second-name{
  user-select: none;
  &:hover{
    color: var(--Brand-Green)
  }
}

.right_main {
    position: relative;
    float: left;
    width: 850px;
    padding: 45px 0 120px 80px;
    min-height: 600px;
    overflow: hidden;
    font-size: 14px;
    box-shadow: 19px 0 32px rgba(143,156,175,.07) inset;
    box-sizing: content-box;
}

.right_main:after {
    content: '';
    position: absolute;
    z-index: 9;
    bottom: -80px;
    left: -19px;
    background: #fff;
    width: 100%;
    height: 100px;
    box-shadow: 0 -80px 80px #fff;
}

.clearfix:after {
    visibility: visible;
}

.con {
    display: none;
    margin-bottom: 60px;
}

.show_con {
    display: block;
}

.title {
    font-size: 36px;
    font-weight: 700;
    cursor: pointer;
    padding: 0 20px;
}

.left_nav ul>li:hover {
    // color: var(--Brand-Green);
    cursor: pointer;
}

.main_content {
    width: 820px;
    margin-top: 30px;
    font-family: semibold;

}

.faq_li {
    border-bottom: 1px solid #f5f5f5;
    cursor: default;
    background: #fff;
}

.faq_ask {
    padding: 20px 0 20px 20px;
    user-select: none;
}
.rate-thanks{
  color: #00C868;
  font-weight: bold;
  font-size: 20px;
}

.rate-btn {
  display: inline-flex;
  width: fit-content;
  height: 32px;

  &__item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    box-sizing: border-box;
    height: 32px;
    color: #fff;
    font: inherit;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    appearance: none;
    background: transparent;
    border: 0;

    &:active {
      opacity: 0.8;
    }
  }

  &__item--helpful {
    width: var(--rate-button-helpful-width, max-content);
    padding: 0 35px 0 16px;
    background: #00c868;
    border-radius: 16px 0 0 16px;
    clip-path: polygon(0 0, calc(100% - 12px) 0, calc(100% - 26px) 100%, 0 100%);
  }

  &__item--not-helpful {
    width: var(--rate-button-width, auto);
    padding: 0 16px 0 28px;
    margin-left: -16px;
    background: #ff6b00;
    border-radius: 0 16px 16px 0;
    clip-path: polygon(12px 0, 100% 0, 100% 100%, 0 100%);
  }
}

.faq_ask em {
    display: inline-block;
    font-style: normal;
    font-size: 20px;
    line-height: 20px;
    width: 760px;
    box-sizing: content-box;
    font-weight: 400;
}

.arrow {
    float: right;
    vertical-align: text-bottom;
    margin-top: 6px;
    margin-right: 20px;
    width: 16px;
    height: 9px;
    background: url(//file.reshg.com/hopegoo/web-pc/images/normal/faq/img/arrow_down.png) no-repeat;
    background-size: 16px 9px;
    box-sizing: content-box;
}

.open_view {
    -ms-transform: rotate(180deg);
    -moz-transform: rotate(180deg);
    -webkit-transform: rotate(180deg);
    -o-transform: rotate(180deg);
    transform: rotate(180deg);
}

.dash-divider{
  width: 100%;
  height: 0;
  border-bottom: 1px dashed #e5e5e5;
  margin: 20px 0;
}
.faq_answer {
    margin: 20px;
    font-size: 14px;
    line-height: 22px;
    color: #000;
}

.show_bg {
    background: #f9fafc;
}

.half_bold {
    font-weight: 500;
}

.faq_page {
    display: flex;
    justify-content: right;
    margin-top: 30px;
    text-align: right;
    padding-right: 32px;

    .hover {
      background-color: #fff;
        color: var(--Brand-Green);
        border: 1px solid var(--Brand-Green);
    }
    :deep(.el-pagination.is-background) .el-pager li {
      &.is-active {
        color: #fff;
        background-color: var(--Brand-Green);
        border-color: var(--Brand-Green);
        font-weight: 400;
      }
      &:hover {
        @extend .hover;
      }
    }
    :deep(.el-pagination)  button:hover {
        @extend .hover;
    }
}

.is-open {
  box-shadow: 0 4px 8px 0 #0000000d;
  z-index: 1;
  position: relative;
  .faq_ask{
    background:#EBF9F1
  }
  // .faq_ask em {
  //   font-weight: 500;
  // }
}
</style>
